'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocale } from '@/components/LocaleProvider'
import { createPortal } from 'react-dom'
import Image from 'next/image'
import type { ReactNode } from 'react'

type DesignItem = {
  id: string
  src: string
  title: string
  subtitle?: string
}

type DesignSection = {
  id: string
  title: string
  items: DesignItem[]
}

function humanizeFilename(filename: string) {
  return filename
    .replace(/\.[^/.]+$/, '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export default function DesignArchive() {
  const { t } = useLocale()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [openAccordions, setOpenAccordions] = useState<Set<number>>(new Set())
  const modalRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const openButtonRef = useRef<HTMLButtonElement>(null)

  const sections: DesignSection[] = useMemo(
    () => [
      {
        id: 'logos',
        title: 'Logos',
        items: [
          'citrii-software-logo.webp',
          'turfsport-logo.webp',
          'farmnode-logo.webp',
          'parow-centre-logo.webp',
          'kalahari-gholf-en-jag-landgoed-logo.webp',
          'river-club-logo.webp',
        ].map((file) => ({
          id: file,
          src: `/images/logos/${file}`,
          title: humanizeFilename(file),
        })),
      },
      {
        id: 'citrii',
        title: 'Citrii',
        items: ['citrii_software_wireframes-2048x1448.webp', 
          'citrii_software_wireframes_2-2048x1448.webp',
          'citrii_software_components-2048x1448.webp', 
          'citrii_scoutlens-1536x1086.webp',
        ].map((file) => ({
          id: file,
          src: `/images/citrii/${file}`,
          title: humanizeFilename(file),
        })),
      },
      {
        id: 'turfsport',
        title: 'Turfsport',
        items: [
          'turfsport_imagery-2048x1448.webp',
          'turfsport_components-2048x1448.webp',
          'turfsport_print-2048x1448.webp',
          'turfsport_wireframes-1536x1086.webp',
        ].map((file) => ({
          id: file,
          src: `/images/turfsport/${file}`,
          title: humanizeFilename(file),
        })),
      },
      {
        id: 'pamgolding',
        title: 'Pam Golding',
        items: [
          'pgp_websites-2048x1448.webp',
          'pgp_ebook-1536x1086.webp',
          'pgp_emails-1-2048x1448.webp',
          'pgp_invitation-1536x1086.webp',
          'pgp.facebook-2048x1448.webp',
        ].map((file) => ({
          id: file,
          src: `/images/pamgolding/${file}`,
          title: humanizeFilename(file),
        })),
      },
    ],
    []
  )

  const openModal = () => {
    setIsModalOpen(true)
    setIsClosing(false)
    setOpenAccordions(new Set([0]))
    document.body.style.overflow = 'hidden'
  }

  const closeModal = () => {
    setIsClosing(true)
    setTimeout(() => {
      setIsModalOpen(false)
      setIsClosing(false)
      setOpenAccordions(new Set())
      document.body.style.overflow = ''
      openButtonRef.current?.focus()
    }, 500)
  }

  const toggleAccordion = (index: number, isLastSection: boolean) => {
    setOpenAccordions((prev) => {
      if (prev.has(index)) {
        if (isLastSection) return prev
        return new Set()
      }
      return new Set([index])
    })
  }

  const renderAccordionSection = (
    sectionIndex: number,
    title: string,
    content: ReactNode,
    isLast: boolean = false,
    rightContent?: ReactNode
  ) => {
    const isOpen = openAccordions.has(sectionIndex)
    return (
      <div
        key={sectionIndex}
        className={`flex flex-col md:flex-row relative md:h-full ${
          isLast ? '' : 'border-b md:border-b-0 border-black/10 dark:border-white/10'
        } transition-all duration-300 ease-in-out bg-background ${
          isOpen ? 'flex-1' : 'md:w-[80px] md:flex-shrink-0'
        }`}
      >
        {!isLast && (
          <div className="hidden md:block absolute right-0 top-0 bottom-0 w-[1px] bg-black/10 dark:bg-white/10 pointer-events-none" />
        )}
        {!isOpen ? (
          <button
            onClick={() => toggleAccordion(sectionIndex, isLast)}
            className="cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 w-full md:w-[80px] md:h-full flex items-start justify-start py-6 md:py-12 px-4 md:px-6 transition-all duration-300 ease-in-out bg-background"
            aria-expanded={isOpen}
            aria-controls={`design-accordion-content-${sectionIndex}`}
            aria-label={`Toggle ${title} section`}
          >
            <h3 className="font-bold text-foreground text-base md:text-lg md:whitespace-nowrap md:[writing-mode:vertical-rl] md:[text-orientation:mixed] md:transform md:rotate-180">
              {title}
            </h3>
          </button>
        ) : (
          <div className="flex flex-col md:flex-row flex-1 md:h-full">
            <button
              onClick={() => toggleAccordion(sectionIndex, isLast)}
              className={`flex-shrink-0 w-full md:w-[80px] flex items-start justify-start py-6 md:py-12 px-4 md:px-6 transition-all duration-300 ease-in-out bg-background ${
                isLast ? 'cursor-default' : 'hover:bg-black/5 dark:hover:bg-white/5'
              }`}
              aria-expanded={isOpen}
              aria-controls={`design-accordion-content-${sectionIndex}`}
              aria-label={`Toggle ${title} section`}
            >
              <h3 className="font-bold text-foreground text-base md:text-lg md:whitespace-nowrap md:[writing-mode:vertical-rl] md:[text-orientation:mixed] md:transform md:rotate-180">
                {title}
              </h3>
            </button>
            <div
              id={`design-accordion-content-${sectionIndex}`}
              className="flex-1 flex flex-col md:flex-row gap-4 md:gap-6 overflow-visible md:overflow-hidden min-h-0 py-6 md:py-12 px-4 md:px-6"
            >
              {content && (
                <div className="flex-1 min-h-0 text-[15px] text-foreground/70 leading-relaxed overflow-visible md:overflow-y-auto scrollbar-hide md:pr-4 text-left">
                  {content}
                </div>
              )}
              {rightContent && (
                <div className={`w-full ${content ? 'md:w-1/2' : 'md:flex-1'} flex-shrink-0 min-h-[260px] md:min-h-0 overflow-visible md:overflow-y-auto overflow-x-hidden md:pr-2`}>
                  <div className="w-full min-h-0">{rightContent}</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  useEffect(() => {
    if (!isModalOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal()
      }
    }

    const handleTab = (e: KeyboardEvent) => {
      if (!modalRef.current) return

      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault()
          lastElement?.focus()
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault()
          firstElement?.focus()
        }
      }
    }

    document.addEventListener('keydown', handleEscape)
    document.addEventListener('keydown', handleTab)

    // Focus the close button when modal opens
    setTimeout(() => {
      closeButtonRef.current?.focus()
    }, 100)

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.removeEventListener('keydown', handleTab)
    }
  }, [isModalOpen])

  return (
    <>
      <section id="design-archive" className="scroll-mt-12 pt-14 pb-14 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-[3em]">
        <h2 className="inline-block text-sm font-bold font-montserrat uppercase bg-[#333333] text-white dark:bg-white dark:text-black px-3 py-1.5 mb-5">{t('sections.designArchive')}</h2>
        <p className="text-[15px] text-black/70 dark:text-white/70 mb-6 leading-relaxed">
          {t('sections.designArchiveSubtitle')}
        </p>
        <button
          ref={openButtonRef}
          onClick={openModal}
          className="text-[15px] font-medium text-black dark:text-white hover:opacity-70 transition-opacity inline-flex items-center gap-1.5 py-2 focus-visible:outline-2 focus-visible:outline-black dark:focus-visible:outline-white focus-visible:outline-offset-2 rounded"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
          {t('sections.viewMore')}
        </button>
      </section>

      {isModalOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={modalRef}
            className="fixed inset-0 z-[10000]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="design-archive-modal-title"
          >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeModal}
            aria-hidden="true"
          />
          <div
            className={`absolute inset-0 bg-background text-foreground ${
              isClosing ? 'animate-slide-out-to-right' : 'animate-slide-in-from-right'
            }`}
          >
            <div className="h-full flex flex-col overflow-hidden">
              {/* Header: back, title, close in one bar */}
              <div className="flex-shrink-0 border-b border-black/10 dark:border-white/10 px-4 md:px-6 py-4 md:py-4 pt-[calc(env(safe-area-inset-top)+1rem)] md:pt-6 flex items-center gap-3 min-h-[56px]">
                <button
                  ref={closeButtonRef}
                  onClick={closeModal}
                  className="flex-shrink-0 text-foreground hover:opacity-70 transition-opacity flex items-center gap-2 min-h-[44px] min-w-[44px] focus-visible:outline-2 focus-visible:outline-black dark:focus-visible:outline-white focus-visible:outline-offset-2 rounded"
                  aria-label={t('modal.backToDesignArchive')}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                  </svg>
                </button>
                <h2
                  id="design-archive-modal-title"
                  className="flex-1 min-w-0 text-base sm:text-lg font-bold text-foreground font-montserrat uppercase text-left leading-tight truncate"
                >
                  {t('modal.designArchiveTitle')}
                </h2>
                <button
                  onClick={closeModal}
                  className="flex-shrink-0 text-foreground hover:opacity-70 transition-opacity min-h-[44px] min-w-[44px] flex items-center justify-center focus-visible:outline-2 focus-visible:outline-black dark:focus-visible:outline-white focus-visible:outline-offset-2 rounded"
                  aria-label={t('modal.close')}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden">
                {sections.map((section, index) =>
                  renderAccordionSection(
                    index,
                    t(`designArchive.${section.id}`),
                    null,
                    index === sections.length - 1,
                    <div
                      className="columns-1 sm:columns-2 lg:columns-3 gap-4 [&>*]:break-inside-avoid [&>*]:mb-4"
                      style={{ columnFill: 'balance' as const }}
                      aria-label={`${t(`designArchive.${section.id}`)} gallery`}
                    >
                      {section.items.map((item) => (
                        <article
                          key={item.id}
                          className="block w-full rounded-lg border border-black/10 dark:border-white/10 hover:border-black/25 dark:hover:border-white/25 transition-colors bg-background overflow-hidden"
                        >
                          <div className="relative w-full min-h-[120px]">
                            <Image
                              src={item.src}
                              alt={item.title}
                              width={400}
                              height={300}
                              className="w-full h-auto object-contain select-none"
                              quality={75}
                              draggable={false}
                            />
                          </div>
                        </article>
                      ))}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>,
          document.body
        )}
    </>
  )
}
